export const audiences = {
  buyers: {
    role: "buyer",
    label: "Buyers",
    verb: "Shop",
    color: "orange",
    eyebrow: "FOR THE EVERYDAY & THE JUST-RAN-OUT",
    title: "Less running around. More living locally.",
    intro:
      "The groceries for tonight. A fresh propane tank for the weekend. The pickup you can’t get to. Bring your local errands together in KwK—and make room for something better.",
    cta: "Find your way into KwK",
    kicker: "Your list. Your choices. Your neighborhood.",
    benefits: [
      [
        "One list, less backtracking",
        "Start with Grocery or Ask KwK, compare local choices, and review your cart before you commit.",
      ],
      [
        "Your household, on the same page",
        "Keep a shared cart, check Pantry restock suggestions, and set up Reorders for the things you buy again.",
      ],
      [
        "A replacement you actually want",
        "Set item preferences and answer a picker’s replacement request. Stay part of the decision when something is missing.",
      ],
      [
        "Make the next visit rewarding",
        "Explore coupons, participating seller offers, and KwK Play. Review each reward’s rules before taking part.",
      ],
    ],
    steps: [
      [
        "Make it yours",
        "Set your address. Arrange applets, folders and widgets around the way you shop.",
      ],
      [
        "Review the whole order",
        "Choose items, quantities and replacement preferences. Review the quote and available delivery options.",
      ],
      [
        "Stay connected",
        "Follow order updates, talk to the people fulfilling it, and find your receipt or get help from the order.",
      ],
    ],
    applets: [
      "Grocery",
      "Ask KwK",
      "Pantry",
      "Household",
      "Reorders",
      "KwK Pay",
      "Rewards",
      "Games",
    ],
    faq: [
      [
        "Can I shop anywhere?",
        "Stores, stock and delivery options depend on your address and local participation. Check the app before planning an order.",
      ],
      [
        "Does every purchase earn points?",
        "No. Points earning depends on an active, eligible program and its published rules. The expanded KwK Points program is being rolled out; games and existing funded offers are separate.",
      ],
      [
        "What if something goes wrong?",
        "Open the order to find communication, receipts and issue-reporting options. Available resolutions depend on the order and the issue.",
      ],
    ],
  },
  drivers: {
    role: "driver",
    label: "Drivers",
    verb: "Drive",
    color: "green",
    eyebrow: "PUT YOURSELF IN THE DRIVER’S SEAT",
    title: "Set your delivery rate. Choose when you go online.",
    intro:
      "Your delivery rate. Your availability. Your decision. Choose your zones, review the pay, time and distance, then accept the work that fits your day.",
    cta: "Explore driving with KwK",
    kicker: "More information. More control.",
    benefits: [
      [
        "See the work before you accept",
        "Review compensation, estimated time, approach and delivery distance, and the pay breakdown. Estimates are not guaranteed hourly earnings.",
      ],
      [
        "Set your delivery rate",
        "Set delivery pricing within the app’s current limits, choose your zones and set minimum offer preferences. Ride fares and Baton Relay use separate service rules.",
      ],
      [
        "Keep your day in view",
        "Bring schedules, daily goals, mileage and expense records together. Review settlement details instead of piecing your day together from memory.",
      ],
      [
        "Go further, without taking every mile",
        "Baton Relay splits suitable routes into accepted legs. Review your leg and handoff commitment before you take it.",
      ],
    ],
    steps: [
      [
        "Set up your working profile",
        "Complete the applicable identity, vehicle, background-check and payout steps.",
      ],
      [
        "Choose your zone and availability",
        "Go online when you’re ready. Review the work, route and compensation on each offer.",
      ],
      [
        "Finish with a record",
        "Follow the pickup and delivery instructions, provide required proof, and review your earnings.",
      ],
    ],
    applets: [
      "Orders",
      "Delivery Zones",
      "Schedule",
      "Baton Relay",
      "Mileage",
      "Settlement",
      "Safety",
      "KwK Pay",
    ],
    faq: [
      [
        "Can I set every price?",
        "You can set your delivery rate within the app’s allowed limits. KwK Ride has a platform-set fare model; Baton Relay has separate outbound-time and return-mileage settings.",
      ],
      [
        "Are earnings guaranteed?",
        "No. Offers, demand, time estimates and costs vary. Review each offer and your expenses; a displayed hourly equivalent is not guaranteed take-home pay.",
      ],
      [
        "Do I have to accept every offer?",
        "KwK gives you offer information and preference controls so you can decide. Review the current service and incentive terms in the app.",
      ],
      [
        "Is every type of work available?",
        "Delivery, courier, curbside and ride opportunities depend on local availability and your eligibility. A service appearing in the app does not guarantee nearby work.",
      ],
    ],
  },
  pickers: {
    role: "picker",
    label: "Pickers",
    verb: "Pick",
    color: "purple",
    eyebrow: "GOOD SHOPPING IS A REAL SKILL",
    title: "Turn a shopping list into a job well done.",
    intro:
      "You know how to choose, compare and find the right replacement. Put that care to work as a KwK personal shopper—with an item-by-item workflow and a direct connection to the buyer.",
    cta: "Explore becoming a picker",
    kicker: "Care in every cart.",
    benefits: [
      [
        "Choose the job with context",
        "Review the store, shopping list, estimated budget and picker fee before taking an available order.",
      ],
      [
        "Shop with a clear checklist",
        "Record found items, quantities and actual shelf prices. Keep missing items and substitutions visible as you go.",
      ],
      [
        "Ask, don’t guess",
        "Follow the buyer’s saved replacement preferences. When approval is required, send the proposal and keep shopping while they decide.",
      ],
      [
        "Close the loop",
        "Capture the receipt, reconcile the items, and track the job through completion and earnings. Eligible verified accounts can access the issuing-card flow.",
      ],
    ],
    steps: [
      [
        "Choose your path",
        "Apply as a personal shopper, or explore the separate store-operator path if you want to manage a proxy store.",
      ],
      [
        "Complete verification",
        "Follow account, consent, background-check and applicable card setup steps. Eligibility and local work availability apply.",
      ],
      [
        "Shop with the buyer",
        "Accept a job, follow the checklist, handle replacements, and submit the checkout receipt.",
      ],
    ],
    applets: [
      "Available jobs",
      "Shopping list",
      "Replacements",
      "Checkout",
      "My Card",
      "Earnings",
      "Chats",
      "KwK Pay",
    ],
    faq: [
      [
        "Is a picker the same as a driver?",
        "They are different roles. A picker shops and handles item-level decisions; delivery is its own workflow. Do not assume a shopping job includes a driving assignment.",
      ],
      [
        "Do I need to fund the shopping myself?",
        "The app has a KwK-issued-card setup path for eligible verified picker accounts. Confirm your account and job’s payment arrangements before shopping.",
      ],
      [
        "Can I run a proxy store?",
        "There is a separate Store Operator onboarding path with business information and operating responsibilities. Ask about availability and terms before getting started.",
      ],
    ],
  },
  sellers: {
    role: "seller",
    label: "Sellers",
    verb: "Sell",
    color: "blue",
    eyebrow: "YOUR BUSINESS. PART OF EVERYDAY LIFE.",
    title: "Be the local shop they come back to.",
    intro:
      "Turn discovery into an order—and an order into a reason to return. KwK connects your catalog, fulfillment, promotions and customer rewards in the same neighborhood experience.",
    cta: "Bring your business to KwK",
    kicker: "Beyond a listing. Into their routine.",
    benefits: [
      [
        "Put your real inventory to work",
        "Manage products, low-stock alerts and store hours. Use Busy Mode to communicate extra preparation time rather than overpromise.",
      ],
      [
        "Create a reason to return",
        "Build coupons, circulars, cashback and flash sales. Explore seller-funded games and the upcoming points program with explicit budgets and terms.",
      ],
      [
        "Keep orders moving",
        "See incoming orders, delivery details and customer communication. Keep your storefront and fulfillment decisions connected.",
      ],
      [
        "Work from the counter or your phone",
        "Use KwK POS for store operations, recorded external payments and supported KwK Pay flows. Review tax settings and transaction records.",
      ],
    ],
    steps: [
      [
        "Create your business profile",
        "Complete seller onboarding, add your location, products and operating hours.",
      ],
      [
        "Make your first offer meaningful",
        "Choose what to promote and what you can fulfill. Set terms, dates and a budget you understand.",
      ],
      [
        "Learn what brings people back",
        "Review orders, sales, product performance and promotions. Adjust your inventory and offers from the same workflow.",
      ],
    ],
    applets: [
      "Orders",
      "Inventory",
      "Flash Sale",
      "Circulars",
      "Store Hours",
      "Analytics",
      "Game Promotions",
      "KwK POS",
    ],
    faq: [
      [
        "What does it cost?",
        "Review the current seller, payment and fulfillment terms during onboarding. This site does not promise a universal commission rate or guaranteed sales.",
      ],
      [
        "Who funds rewards?",
        "Participating sellers or sponsors fund eligible offers. Published campaign budgets, inventory, caps and redemption terms define the commitment—not a promise of unlimited KwK-funded rewards.",
      ],
      [
        "Does KwK replace my card terminal?",
        "Do not assume that. Recorded cash or external-card sales are distinct from payment collection. KwK Pay is a separate supported payment flow; configure tax and collection settings for your store.",
      ],
    ],
  },
};

export const gameCatalog = [
  {
    id: "block",
    name: "Starlight Blocks",
    genre: "A little space to think",
    detail:
      "Place shapes, build constellations and explore your own puzzle journey.",
    symbol: "star",
    color: "night",
  },
  {
    id: "market",
    name: "Market Rush",
    genre: "Match three",
    detail: "Swap produce, build combinations and let the next cascade unfold.",
    image: "/play/market.png",
    color: "violet",
  },
  {
    id: "pairs",
    name: "Pantry Pairs",
    genre: "Memory challenge",
    detail:
      "Remember what’s hiding in the pantry. Find the pairs, one careful choice at a time.",
    image: "/play/pairs.png",
    color: "peach",
  },
  {
    id: "quick",
    name: "Quickfire",
    genre: "Timed trivia",
    detail:
      "A quick question, a little pressure, and another chance to test what you know.",
    image: "/play/quick.png",
    color: "violet",
  },
  {
    id: "route",
    name: "Route Rush",
    genre: "Arcade chase",
    detail:
      "Steer through neighborhood streets, collect parcels and keep the chase moving.",
    image: "/play/route.png",
    color: "blue",
  },
];
